/**
 * Analytics Module - NRI PathFinder
 *
 * Provides unified interface for:
 * - Google Analytics 4 (user behavior, funnels)
 * - Custom event tracking
 *
 * Critical Metrics Tracked:
 * 1. Funnel: Landing → Wizard → Dashboard → Journey
 * 2. Wizard step completion rates
 * 3. Feature usage (tabs, CTAs, exports)
 * 4. Conversion events (email capture, journey generation)
 */

// ============================================
// Types
// ============================================

export type WizardStep = 1 | 2 | 3 | 4 | 5 | 6

export type DashboardTab =
  | 'summary'
  | 'tax'
  | 'projections'
  | 'whatif'
  | 'checklist'
  | 'journey'

export type CTAType =
  | 'build_roadmap'           // Landing page main CTA
  | 'rnor_calculator'         // Standalone tool
  | 'city_comparison'         // Standalone tool
  | 'ca_consult'              // CA consultation CTA
  | 'newsletter'              // Newsletter signup
  | 'journey_generate'        // Generate journey button
  | 'journey_export_pdf'      // PDF export
  | 'journey_share'           // Share journey
  | 'substack'                // Substack link

export type CountryCode = 'USA' | 'UAE'

export type ErrorSeverity = 'low' | 'medium' | 'high' | 'critical'

// ============================================
// GA4 Event Types (for type safety)
// ============================================

interface GA4Event {
  event_category?: string
  event_label?: string
  value?: number
  [key: string]: string | number | boolean | undefined
}

// ============================================
// Global gtag declaration
// ============================================

declare global {
  interface Window {
    gtag: (
      command: 'config' | 'event' | 'js' | 'set',
      targetId: string | Date,
      params?: GA4Event | { [key: string]: unknown }
    ) => void
    dataLayer: unknown[]
  }
}

// ============================================
// Analytics Class
// ============================================

class Analytics {
  private initialized = false
  private debugMode = false

  /**
   * Initialize analytics (call once in app entry)
   * Note: gtag('config') is already called in index.html for initial pageview
   * This just enables our custom event tracking
   */
  init(measurementId: string, debug = false) {
    this.debugMode = debug

    if (typeof window === 'undefined') return
    if (this.initialized) return

    // Check if gtag already loaded
    if (!window.gtag) {
      if (import.meta.env.DEV) {
        console.warn('[Analytics] GA4 not loaded - ensure gtag script is in index.html')
      }
      return
    }

    // gtag config is already done in index.html
    // We just mark ourselves as ready for custom events
    this.initialized = true
    // Only log in development
    if (import.meta.env.DEV && this.debugMode) {
      this.log(`Analytics ready: ${measurementId}`)
    }
  }

  /**
   * Track page view (for SPA navigation)
   */
  pageView(path: string, title?: string) {
    this.track('page_view', {
      page_path: path,
      page_title: title || document.title,
    })
  }

  // ==========================================
  // FUNNEL EVENTS
  // ==========================================

  /**
   * User starts the wizard from landing page
   */
  wizardStart(source: 'landing_hero' | 'landing_cta' | 'nav' | 'direct') {
    this.track('wizard_start', {
      event_category: 'funnel',
      source,
    })
  }

  /**
   * User completes a wizard step
   */
  wizardStepComplete(step: WizardStep, country?: CountryCode) {
    this.track('wizard_step_complete', {
      event_category: 'funnel',
      step,
      country: country || 'unknown',
      // Value = step number for funnel analysis
      value: step,
    })
  }

  /**
   * User abandons wizard (leaves before completion)
   */
  wizardAbandon(lastStep: WizardStep, reason?: string) {
    this.track('wizard_abandon', {
      event_category: 'funnel',
      last_step: lastStep,
      reason: reason || 'unknown',
    })
  }

  /**
   * User completes entire wizard → sees dashboard
   */
  wizardComplete(country: CountryCode, totalTimeSeconds: number) {
    this.track('wizard_complete', {
      event_category: 'funnel',
      event_label: 'conversion',
      country,
      duration_seconds: totalTimeSeconds,
      value: 1, // Conversion value
    })
  }

  // ==========================================
  // DASHBOARD EVENTS
  // ==========================================

  /**
   * User switches dashboard tab
   */
  dashboardTabView(tab: DashboardTab) {
    this.track('dashboard_tab_view', {
      event_category: 'engagement',
      tab,
    })
  }

  /**
   * User interacts with What-If scenario
   */
  whatIfInteraction(scenarioType: 'modify' | 'save' | 'compare' | 'quick') {
    this.track('whatif_interaction', {
      event_category: 'engagement',
      action: scenarioType,
    })
  }

  // ==========================================
  // JOURNEY (PREMIUM) EVENTS
  // ==========================================

  /**
   * User requests Journey generation
   */
  journeyRequested(country: CountryCode) {
    this.track('journey_requested', {
      event_category: 'premium',
      event_label: 'conversion',
      country,
    })
  }

  /**
   * Journey generated successfully
   */
  journeyGenerated(country: CountryCode, durationMs: number, cached: boolean) {
    this.track('journey_generated', {
      event_category: 'premium',
      event_label: 'success',
      country,
      duration_ms: durationMs,
      cached: cached ? 'yes' : 'no',
      value: cached ? 0 : 1, // Only count non-cached as conversion
    })
  }

  /**
   * Journey generation failed
   */
  journeyFailed(country: CountryCode, errorType: string, errorMessage: string) {
    this.track('journey_failed', {
      event_category: 'premium',
      event_label: 'error',
      country,
      error_type: errorType,
      error_message: errorMessage.substring(0, 100), // Truncate
    })
  }

  /**
   * User exports Journey PDF
   */
  journeyExported(format: 'pdf' | 'share') {
    this.track('journey_exported', {
      event_category: 'premium',
      format,
    })
  }

  // ==========================================
  // CONVERSION EVENTS
  // ==========================================

  /**
   * Email captured (journey gate, newsletter, etc.)
   */
  emailCaptured(source: 'journey_gate' | 'newsletter' | 'ca_consult') {
    this.track('email_captured', {
      event_category: 'conversion',
      event_label: source,
      source,
      value: 1,
    })
  }

  /**
   * CTA clicked
   */
  ctaClick(cta: CTAType, location: string) {
    this.track('cta_click', {
      event_category: 'engagement',
      cta_type: cta,
      location,
    })
  }

  // ==========================================
  // USER PROFILE EVENTS
  // ==========================================

  /**
   * Track user profile for segmentation
   */
  setUserProperties(props: {
    country?: CountryCode
    targetCity?: string
    ageGroup?: string
    hasESOP?: boolean
    has401k?: boolean
    fireStatus?: string
  }) {
    if (!window.gtag) return

    window.gtag('set', 'user_properties', {
      country: props.country,
      target_city: props.targetCity,
      age_group: props.ageGroup,
      has_esop: props.hasESOP ? 'yes' : 'no',
      has_401k: props.has401k ? 'yes' : 'no',
      fire_status: props.fireStatus,
    })

    this.log('User properties set', props)
  }

  // ==========================================
  // ERROR EVENTS (complement Sentry)
  // ==========================================

  /**
   * Track errors in GA4 (for error rate metrics)
   */
  error(category: string, message: string, severity: ErrorSeverity = 'medium') {
    this.track('app_error', {
      event_category: 'error',
      error_category: category,
      error_message: message.substring(0, 100),
      severity,
    })
  }

  // ==========================================
  // PERFORMANCE EVENTS
  // ==========================================

  /**
   * Track page load performance
   */
  performance(metric: 'LCP' | 'FID' | 'CLS' | 'TTFB', value: number) {
    this.track('web_vitals', {
      event_category: 'performance',
      metric_name: metric,
      value: Math.round(value),
    })
  }

  // ==========================================
  // INTERNAL
  // ==========================================

  private track(eventName: string, params: GA4Event) {
    if (typeof window === 'undefined') return

    if (!window.gtag) {
      this.log(`[SKIP] ${eventName}`, params)
      return
    }

    window.gtag('event', eventName, {
      ...params,
      timestamp: Date.now(),
    })

    this.log(`[TRACK] ${eventName}`, params)
  }

  private log(message: string, data?: unknown) {
    // Only log in development mode when debug is enabled
    if (this.debugMode && import.meta.env.DEV) {
      console.log(`[Analytics] ${message}`, data || '')
    }
  }
}

// ============================================
// Singleton Export
// ============================================

export const analytics = new Analytics()

// ============================================
// React Hook for easy access
// ============================================

export function useAnalytics() {
  return analytics
}
